import { CSSProperties, ReactNode, useContext, useState } from "react";
import CardSymphony from "elements/CardSymphony";
import useWebsiteBuilder, { CollectDataInputType, WebsiteBuilder_collectDataExists } from "Hooks/useWebsiteBuilder";
import { capitalizeFirstLetter, clsx, makeKey, styx } from "modules/Utils";
import WebsiteBuilderHeader from "../WebsiteBuilderHeader";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import Icon from "elements/Icon";
import Colors from "modules/Colors";
import { DraggableList } from "components/shareable/DraggableList";
import Sty from "modules/Sty";
import ModalSymphony from "elements/ModalSymphony";
import WebsiteBuilderFooter from "../WebsiteBuilderFooter";
import WebsiteBuilderEmptyState from "../WebsiteBuilderEmptyState";
import SwitchSymphony from "elements/SwitchSymphony";
import useScreen from "Hooks/useScreen";
import useBrand from "Hooks/useBrand";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";

interface CollectDataContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: ReactNode;
    id: string;
}

const CollectDataContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: CollectDataContentProps) => {
    const {
        mobileView,
    } = useScreen();

    const {
        isPaidBrand
    } = useBrand();

    const {
        websiteContent,
        setWebsiteContent,
    } = useWebsiteBuilder() as WebsiteBuilder_collectDataExists;

    const { handleOpenModal } = useContext(UpgradeModalContext);

    const collectDataContent = websiteContent[contentIndex].collectDataContent;

    const requiresPaidPlan = collectDataContent.info.length > 2;

    const [selectedInfoIndex, setSelectedInfoIndex] = useState<number | null>(null);
    const [showEditInfoModal, setShowEditInfoModal] = useState<boolean>(false);

    const addField = () => {
        if (!isPaidBrand) {
            handleOpenModal();
            return;
        }

        const websiteContentLocal = [...websiteContent];
        websiteContentLocal[contentIndex].collectDataContent.info.push({
            title: 'New Field',
            placeholder: 'Enter placeholder text',
            type: CollectDataInputType.TEXT,
            isRequired: false,
        });

        setWebsiteContent(websiteContentLocal);
        const newInfoIndex = websiteContentLocal[contentIndex].collectDataContent.info.length - 1;
        setSelectedInfoIndex(newInfoIndex);
        setShowEditInfoModal(true);
    }

    const renderEditInfoModal = () => {
        if (selectedInfoIndex === null) {
            return null;
        }

        const info = collectDataContent?.info[selectedInfoIndex];

        return (
            <ModalSymphony
                className="contactFormContentEditInfoModal"
                style={{ gap: 16 }}
                isOpen={showEditInfoModal}
                onClose={() => {
                    setSelectedInfoIndex(null);
                    setShowEditInfoModal(false)
                }}
                title="Edit Field"
                onClickButtonRight={() => {
                    setSelectedInfoIndex(null);
                    setShowEditInfoModal(false)
                }}
                buttonRightText="Done"
                buttonRightVariant="text"
            >
                <InputSymphony
                    className="contactFormContentInputContactType"
                    label="Contact Type"
                    value={info.type}
                    isSelectDropdown
                    maxWidth={180}
                    options={Object.values(CollectDataInputType).map(type => ({
                        title: capitalizeFirstLetter(type),
                        value: type
                    })) as SelectOptionItem[]}
                    onOptionSelect={(option) => {
                        const websiteContentLocal = [...websiteContent];
                        websiteContentLocal[contentIndex].collectDataContent.info[selectedInfoIndex].type = option.value as CollectDataInputType;
                        setWebsiteContent(websiteContentLocal);
                    }}
                />
                {info.type !== CollectDataInputType.EMAIL_PHONE ?
                    <>
                        <InputSymphony
                            className="contactFormContentInputTitle"
                            label="Title"
                            value={info.title}
                            placeholder="Enter input title"
                            onChange={(value) => {
                                const websiteContentLocal = [...websiteContent];
                                websiteContentLocal[contentIndex].collectDataContent.info[selectedInfoIndex].title = value;
                                setWebsiteContent(websiteContentLocal);
                            }}
                        />
                        <InputSymphony
                            className="contactFormContentInputPlaceholder"
                            label="Placeholder"
                            value={info.placeholder || ''}
                            placeholder="Enter placeholder text"
                            onChange={(value) => {
                                const websiteContentLocal = [...websiteContent];
                                websiteContentLocal[contentIndex].collectDataContent.info[selectedInfoIndex].placeholder = value;
                                setWebsiteContent(websiteContentLocal);
                            }}
                        />
                    </>
                    :
                    <SwitchSymphony
                        className="contactFormContentInputRequiredSwitch"
                        checked={collectDataContent?.isBothEmailPhone}
                        onChange={(value) => {
                            const websiteContentLocal = [...websiteContent];
                            websiteContentLocal[contentIndex].collectDataContent.isBothEmailPhone = value;
                            setWebsiteContent(websiteContentLocal);
                        }}
                        label="Show both Email & Phone"
                        labelPlacement="right"
                    />
                }
            </ModalSymphony>
        )
    }

    // NOTE: render()
    return (
        <CardSymphony
            id={id}
            className={clsx("contactFormContentComponent", className)}
            style={styx(sty.contactFormContentComponent)}
            size="small"
        >
            <WebsiteBuilderHeader
                contentIndex={contentIndex}
                rightSideContent={
                    <>
                        {tabSelectorComponent}
                        <ButtonSymphony
                            className="contactFormContentAddFieldButton"
                            iconLeft="addCircle"
                            variant="text"
                            onClick={addField}
                            iconRight={isPaidBrand ? undefined : "gem"}
                        >
                            {mobileView ? '' : 'Add Field'}
                        </ButtonSymphony>
                    </>
                }
            />
            {!collectDataContent?.info.length &&
                <WebsiteBuilderEmptyState
                    className="contactFormContentEmptyState"
                    title="No fields added"
                    description="Add a field to your collect data content to get started."
                    isHorizontal
                    buttonText="Add Field"
                    onClickButton={addField}
                />
            }
            <DraggableList
                list={collectDataContent?.info}
                keyOfItem="title"
                updateList={(list) => {
                    const websiteContentLocal = [...websiteContent];
                    websiteContentLocal[contentIndex].collectDataContent.info = list;
                    setWebsiteContent(websiteContentLocal);
                }}
                isDragHandle
                renderItem={(info, index, dragHandleProps) => {
                    return (
                        <div
                            key={makeKey('collect-data-input', index, contentIndex)}
                            className="contactFormContentInputContainer"
                            style={sty.contactFormContentInputContainer}
                        >
                            <div
                                {...dragHandleProps}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                }}
                            >
                                <Icon
                                    className="contactFormContentInputDragIcon"
                                    icon="drag"
                                    size={20}
                                    color={Colors.grey}
                                />
                            </div>
                            <div className="contactFormContentInputTitle" style={Sty.text16grey}>
                                {info.title}
                            </div>

                            <SwitchSymphony
                                className="contactFormContentInputRequiredSwitch"
                                style={{ marginLeft: 'auto' }}
                                checked={info.isRequired}
                                onChange={(value) => {
                                    const websiteContentLocal = [...websiteContent];
                                    websiteContentLocal[contentIndex].collectDataContent.info[index].isRequired = value;
                                    setWebsiteContent(websiteContentLocal);
                                }}
                                label={`${info.isRequired ? 'Required' : ''}`}
                                labelPlacement="left"
                            />
                            <Icon
                                className="contactFormContentInputEditIcon"
                                icon="edit"
                                color={Colors.greyDark}
                                onClick={() => {
                                    if (isPaidBrand) {
                                        setSelectedInfoIndex(index);
                                        setShowEditInfoModal(true);
                                    } else {
                                        handleOpenModal();
                                    }
                                }}
                            />
                            <Icon
                                className="contactFormContentInputTrashIcon"
                                icon="closeCircle"
                                color={Colors.greyDark}
                                onClick={() => {
                                    if (isPaidBrand) {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].collectDataContent.info.splice(index, 1);
                                        setWebsiteContent(websiteContentLocal);
                                    } else {
                                        handleOpenModal();
                                    }
                                }}
                            />
                        </div>
                    )
                }}
            />

            {renderEditInfoModal()}

            <SwitchSymphony
                className="contactFormContentInputShowTitlesSwitch"
                checked={collectDataContent?.isShowTitles}
                label="Show Titles"
                labelPlacement="right"
                onChange={(value) => {
                    const websiteContentLocal = [...websiteContent];
                    websiteContentLocal[contentIndex].collectDataContent.isShowTitles = value;
                    setWebsiteContent(websiteContentLocal);
                }}
            />

            <InputSymphony
                className="contactFormContentInput"
                label="Button Text"
                value={collectDataContent?.buttonText || ''}
                placeholder="Enter button text"
                onChange={(value) => {
                    const websiteContentLocal = [...websiteContent];
                    websiteContentLocal[contentIndex].collectDataContent.buttonText = value;
                    setWebsiteContent(websiteContentLocal);
                }}
            />
            <InputSymphony
                className="contactFormContentInput"
                label="Post Subscribe Message"
                value={collectDataContent?.postSubscribeMessage || ''}
                placeholder="Enter post subscribe message"
                onChange={(value) => {
                    const websiteContentLocal = [...websiteContent];
                    websiteContentLocal[contentIndex].collectDataContent.postSubscribeMessage = value;
                    setWebsiteContent(websiteContentLocal);
                }}
            />
            <WebsiteBuilderFooter
                className="contactFormContentFooter"
                contentIndex={contentIndex}
                isBackgroundColor
                isSecondaryColor
                isTextColor
                isTextSecondaryColor
                requiresPaidPlan={requiresPaidPlan}
                titleRequired="The Collect Data content has more than 2 fields, this requires a paid plan to publish your website."
            />
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    contactFormContentComponent: {
    },
    contactFormContentInputContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        gap: 8,
        padding: 16,
        borderRadius: 8,
        border: Sty.border,
    }
}

export default CollectDataContent;